/* Chapter navigation shared by the nine legal guide pages. */
if (!customElements.get('legal-guide-document')) {
  customElements.define('legal-guide-document', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.chapters = [...this.querySelectorAll('.legal-guide__chapter')];
      this.links = [...this.querySelectorAll('.legal-guide__nav-link')];
      this.classList.add('is-ready');

      this.addEventListener('click', event => {
        const navToggle = event.target.closest('.legal-guide__nav-toggle');
        if (navToggle) {
          const panel = this.querySelector('#' + CSS.escape(navToggle.getAttribute('aria-controls')));
          panel.hidden = !panel.hidden;
          navToggle.setAttribute('aria-expanded', String(!panel.hidden));
          return;
        }

        const link = event.target.closest('a[href^="#"]');
        if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        const target = this.resolveTarget(link.getAttribute('href'));
        if (!target) return;
        event.preventDefault();
        this.scrollToTarget(target, window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth');
        if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
        target.focus({ preventScroll: true });
        history.replaceState(null, '', '#' + target.id);
      }, { signal });

      const openHash = () => {
        const target = this.resolveTarget(window.location.hash);
        if (!target) return;
        requestAnimationFrame(() => {
          if (this.isConnected) this.scrollToTarget(target, 'instant');
        });
      };
      window.addEventListener('hashchange', openHash, { signal });
      openHash();

      let frame = 0;
      const updateIndex = () => {
        frame = 0;
        let current = this.chapters[0];
        for (const chapter of this.chapters) {
          if (chapter.getBoundingClientRect().top <= this.scrollOffset(chapter) + 2) current = chapter;
        }
        this.links.forEach(link => {
          const active = current && link.dataset.chapterIndex === current.dataset.chapterIndex;
          link.classList.toggle('is-active', Boolean(active));
          if (active) link.setAttribute('aria-current', 'location');
          else link.removeAttribute('aria-current');
        });
      };
      window.addEventListener('scroll', () => {
        if (!frame) frame = requestAnimationFrame(updateIndex);
      }, { passive: true, signal });
      window.addEventListener('resize', updateIndex, { signal });
      updateIndex();
    }

    scrollOffset(target) {
      const chapter = target.closest('.legal-guide__chapter') || target;
      const margin = parseFloat(getComputedStyle(chapter).scrollMarginTop) || 0;
      const headerHeight = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
      return Math.max(margin, headerHeight) + 16;
    }

    scrollToTarget(target, behavior) {
      const top = Math.max(0, target.getBoundingClientRect().top + window.scrollY - this.scrollOffset(target));
      window.scrollTo({ top, behavior });
    }

    resolveTarget(hash) {
      if (!hash || hash === '#') return null;
      let id;
      try { id = decodeURIComponent(hash.slice(1)); } catch { return null; }
      const shortAnchor = /^section-(\d+)$/.exec(id);
      if (shortAnchor) return this.chapters.find(chapter => chapter.dataset.chapterIndex === shortAnchor[1]);
      const target = document.getElementById(id);
      return target && this.contains(target) ? target : null;
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
    }
  });
}
